import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { apiFetch, API } from "@/utils/api";

export default function Login() {
  const navigate = useNavigate();
  // Vistas: "login" | "register" | "register-done" | "forgot" | "forgot-done"
  const [view, setView] = useState("login");
  const [signupEnabled, setSignupEnabled] = useState(false);
  const [invitationRequired, setInvitationRequired] = useState(true);
  const [googleEnabled, setGoogleEnabled] = useState(false);

  // ---- Login ----
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [verifyNotice, setVerifyNotice] = useState("");
  const [resendState, setResendState] = useState("idle"); // idle | sending | sent
  const [loading, setLoading] = useState(false);

  // ---- Registro ----
  const [regName, setRegName] = useState("");
  const [regEmail, setRegEmail] = useState("");
  const [regPassword, setRegPassword] = useState("");
  const [regInvite, setRegInvite] = useState("");
  const [regLoading, setRegLoading] = useState(false);

  // ---- Recuperar contraseña ----
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const checkSignup = async () => {
      try {
        const response = await apiFetch(`${API}/auth/signup-status`);
        if (!response.ok) return;
        const data = await response.json();
        if (!cancelled) {
          setSignupEnabled(Boolean(data?.signup_enabled));
          setInvitationRequired(data?.invitation_required !== false);
          setGoogleEnabled(data?.google_enabled !== false);
        }
      } catch {
        // Silencioso: sin signup-status simplemente no se ofrece registro
      }
    };
    checkSignup();
    return () => {
      cancelled = true;
    };
  }, []);

  // Reenvío del enlace de verificación desde el propio aviso 403 (roadmap 1.5).
  const handleResendVerification = async () => {
    if (!email.trim()) return;
    setResendState("sending");
    try {
      await apiFetch(`${API}/auth/resend-verification`, {
        method: "POST",
        body: JSON.stringify({ email: email.trim() }),
      });
    } catch {
      // Respuesta neutra en cualquier caso: no revelamos si la cuenta existe.
    }
    setResendState("sent");
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setVerifyNotice("");
    setResendState("idle");
    setLoading(true);

    try {
      const response = await apiFetch(`${API}/auth/login`, {
        method: "POST",
        body: JSON.stringify({ email, password }),
      });

      if (response.ok) {
        navigate("/dashboard");
      } else {
        const data = await response.json().catch(() => null);
        if (response.status === 403 && data?.detail) {
          // Cuenta sin verificar: mensaje claro, no un error genérico
          setVerifyNotice(data.detail);
        } else {
          setError(data?.detail || "Credenciales incorrectas");
        }
      }
    } catch (err) {
      if (err.message !== "Not authenticated") {
        setError("Error de conexión. Intenta de nuevo.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (regPassword.length < 12) {
      toast.error("La contraseña debe tener al menos 12 caracteres");
      return;
    }
    setRegLoading(true);
    try {
      const response = await apiFetch(`${API}/auth/register`, {
        method: "POST",
        body: JSON.stringify({
          email: regEmail,
          name: regName,
          password: regPassword,
          invite_code: regInvite,
        }),
      });
      if (response.ok) {
        const data = await response.json();
        if (data.email_verified) navigate("/dashboard");
        else setView("register-done");
      } else {
        const data = await response.json().catch(() => null);
        toast.error(data?.detail || "No se pudo crear la cuenta. Intenta de nuevo.");
      }
    } catch (err) {
      if (err.message !== "Not authenticated") {
        toast.error("Error de conexión. Intenta de nuevo.");
      }
    } finally {
      setRegLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    try {
      await apiFetch(`${API}/auth/forgot-password`, {
        method: "POST",
        body: JSON.stringify({ email: forgotEmail }),
      });
    } catch {
      // Respuesta neutra siempre: no revelamos si el email existe
    } finally {
      setForgotLoading(false);
      setView("forgot-done");
    }
  };

  const handleGoogleLogin = () => {
    window.location.href = `${API}/auth/google/login`;
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left side - Image */}
      <div
        className="hidden lg:flex lg:w-1/2 relative"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1673691186762-5611baa346d7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwyfHxtaW5pbWFsaXN0JTIwbW9kZXJuJTIwb2ZmaWNlJTIwYXJjaGl0ZWN0dXJlJTIwd2hpdGUlMjBicmlnaHR8ZW58MHx8fHwxNzcwMjA1NTM0fDA&ixlib=rb-4.1.0&q=85')`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-slate-900/10" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <h2 className="font-heading text-3xl font-bold mb-2">
            Contabilidad automatizada
          </h2>
          <p className="text-white/80">
            Gestiona tu S.L. con el Plan General Contable español
          </p>
        </div>
      </div>

      {/* Right side - Forms */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          <div className="text-center mb-10">
            <h1 className="font-heading text-3xl font-bold text-slate-900 mb-2">
              LiquidArc
            </h1>
            <p className="text-slate-500">
              Contabilidad PGC para tu empresa
            </p>
          </div>

          {view === "login" && (
            <div className="space-y-6">
              {/* Login Form */}
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="tu@empresa.com"
                    required
                    data-testid="email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="password">Contraseña</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Tu contraseña"
                      required
                      className="pr-10"
                      data-testid="password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                      aria-pressed={showPassword}
                      title={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                      data-testid="toggle-password"
                      className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-slate-400 hover:text-slate-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 rounded-r-md"
                    >
                      {showPassword
                        ? <EyeOff className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" />
                        : <Eye className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" />}
                    </button>
                  </div>
                </div>

                {error && (
                  <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="login-error">
                    {error}
                  </div>
                )}

                {verifyNotice && (
                  <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 space-y-2" data-testid="login-verify-notice">
                    <p>{verifyNotice} Revisa tu bandeja de entrada (y la carpeta de spam) para encontrar el enlace de verificación.</p>
                    {resendState === "sent" ? (
                      <p className="text-emerald-700" data-testid="login-resend-sent">
                        Si la cuenta está pendiente de verificar, hemos enviado un nuevo enlace a {email}.
                      </p>
                    ) : (
                      <button
                        type="button"
                        onClick={handleResendVerification}
                        disabled={resendState === "sending"}
                        className="underline font-medium hover:text-amber-900 disabled:opacity-60"
                        data-testid="login-resend-btn"
                      >
                        {resendState === "sending" ? "Enviando..." : "¿Enlace caducado o perdido? Reenviar email de verificación"}
                      </button>
                    )}
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white font-medium"
                  data-testid="login-submit-btn"
                >
                  {loading ? "Iniciando sesión..." : "Iniciar sesión"}
                </Button>
              </form>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => setView("forgot")}
                  className="text-sm text-slate-500 hover:text-slate-900 underline underline-offset-4"
                  data-testid="forgot-password-link"
                >
                  ¿Olvidaste tu contraseña?
                </button>
              </div>

              {/* Divider */}
              {googleEnabled && <>
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-slate-200" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-2 text-slate-500">o</span>
                </div>
              </div>

              {/* Google OAuth */}
              <Button
                onClick={handleGoogleLogin}
                variant="outline"
                className="w-full h-12 font-medium"
                data-testid="google-login-btn"
              >
                <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="currentColor"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                Continuar con Google
              </Button>
              </>}

              {signupEnabled && (
                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => setView("register")}
                    className="text-sm text-slate-600 hover:text-slate-900 font-medium underline underline-offset-4"
                    data-testid="signup-toggle"
                  >
                    {invitationRequired ? "¿Tienes un código de invitación? Crea tu cuenta" : "Crear la primera cuenta"}
                  </button>
                </div>
              )}

              <p className="text-center text-xs text-slate-500">
                Al continuar, aceptas nuestros{" "}
                <a href="/terminos" target="_blank" rel="noopener" className="underline hover:text-slate-700" data-testid="terms-link">
                  términos de servicio
                </a>{" "}
                y nuestra{" "}
                <a href="/privacidad" target="_blank" rel="noopener" className="underline hover:text-slate-700" data-testid="privacy-link">
                  política de privacidad
                </a>
              </p>
            </div>
          )}

          {view === "register" && (
            <div className="space-y-6">
              <form onSubmit={handleRegister} className="space-y-4">
                <div>
                  <Label htmlFor="reg-name">Nombre</Label>
                  <Input
                    id="reg-name"
                    type="text"
                    value={regName}
                    onChange={(e) => setRegName(e.target.value)}
                    placeholder="Tu nombre"
                    required
                    data-testid="signup-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="reg-email">Email</Label>
                  <Input
                    id="reg-email"
                    type="email"
                    value={regEmail}
                    onChange={(e) => setRegEmail(e.target.value)}
                    placeholder="tu@empresa.com"
                    required
                    data-testid="signup-email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="reg-password">Contraseña</Label>
                  <Input
                    id="reg-password"
                    type="password"
                    value={regPassword}
                    onChange={(e) => setRegPassword(e.target.value)}
                    placeholder="Mínimo 12 caracteres"
                    required
                    minLength={12}
                    data-testid="signup-password-input"
                  />
                  <p className="text-xs text-slate-400 mt-1">
                    Al menos 12 caracteres. Puedes usar una frase fácil de recordar.
                  </p>
                </div>
                {invitationRequired && <div>
                  <Label htmlFor="reg-invite">Código de invitación</Label>
                  <Input
                    id="reg-invite"
                    type="text"
                    value={regInvite}
                    onChange={(e) => setRegInvite(e.target.value)}
                    placeholder="Tu código de invitación"
                    required
                    data-testid="signup-invite-input"
                  />
                </div>}

                <Button
                  type="submit"
                  disabled={regLoading}
                  className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white font-medium"
                  data-testid="signup-submit-btn"
                >
                  {regLoading ? "Creando cuenta..." : "Crear cuenta"}
                </Button>
              </form>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => setView("login")}
                  className="text-sm text-slate-500 hover:text-slate-900 underline underline-offset-4"
                  data-testid="signup-back-to-login"
                >
                  ¿Ya tienes cuenta? Inicia sesión
                </button>
              </div>

              <p className="text-center text-xs text-slate-500">
                Al crear la cuenta aceptas nuestros{" "}
                <a href="/terminos" target="_blank" rel="noopener" className="underline hover:text-slate-700">
                  términos de servicio
                </a>{" "}
                y nuestra{" "}
                <a href="/privacidad" target="_blank" rel="noopener" className="underline hover:text-slate-700">
                  política de privacidad
                </a>
              </p>
            </div>
          )}

          {view === "register-done" && (
            <div className="space-y-6 text-center" data-testid="signup-check-email">
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-6">
                <h2 className="font-heading text-xl font-semibold text-slate-900 mb-2">
                  Revisa tu correo
                </h2>
                <p className="text-sm text-slate-600">
                  Hemos enviado un enlace de verificación a{" "}
                  <span className="font-medium text-slate-900">{regEmail}</span>.
                  Abre el enlace para activar tu cuenta y poder iniciar sesión.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setView("login")}
                className="text-sm text-slate-500 hover:text-slate-900 underline underline-offset-4"
                data-testid="signup-done-back-to-login"
              >
                Volver a iniciar sesión
              </button>
            </div>
          )}

          {view === "forgot" && (
            <div className="space-y-6">
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <p className="text-sm text-slate-600">
                  Escribe el email de tu cuenta y te enviaremos instrucciones para
                  restablecer la contraseña.
                </p>
                <div>
                  <Label htmlFor="forgot-email">Email</Label>
                  <Input
                    id="forgot-email"
                    type="email"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    placeholder="tu@empresa.com"
                    required
                    data-testid="forgot-email-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={forgotLoading}
                  className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white font-medium"
                  data-testid="forgot-submit-btn"
                >
                  {forgotLoading ? "Enviando..." : "Enviar instrucciones"}
                </Button>
              </form>
              <div className="text-center">
                <button
                  type="button"
                  onClick={() => setView("login")}
                  className="text-sm text-slate-500 hover:text-slate-900 underline underline-offset-4"
                  data-testid="forgot-back-to-login"
                >
                  Volver a iniciar sesión
                </button>
              </div>
            </div>
          )}

          {view === "forgot-done" && (
            <div className="space-y-6 text-center" data-testid="forgot-done-message">
              <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-6">
                <h2 className="font-heading text-xl font-semibold text-slate-900 mb-2">
                  Solicitud enviada
                </h2>
                <p className="text-sm text-slate-600">
                  Si el email existe, hemos enviado instrucciones.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setView("login")}
                className="text-sm text-slate-500 hover:text-slate-900 underline underline-offset-4"
                data-testid="forgot-done-back-to-login"
              >
                Volver a iniciar sesión
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
